import { useEffect, useState } from "react";
import { runAssetSelfTest } from "@/lib/asset-self-test";
import { runCssSentinel } from "@/lib/css-sentinel";
import { sendDiagnosticTelemetry } from "@/lib/diagnostic-telemetry";
import { useAuth } from "@/lib/auth";

type Issue = {
  title: string;
  causes: string[];
  severe: boolean;
  steps: string[];
};

type Signals = {
  capturedAt: string;
  buildMode: "dev" | "prod" | "unknown";
  hasViteDevMarker: boolean;
  hasProdAssetJs: boolean;
  hasProdAssetCss: boolean;
  hasProdPreload: boolean;
  hiddenWorks: boolean;
  loadedSheets: number;
  hasDesignTokens: boolean;
  stylesheetLinks: number;
  scriptCount: number;
  viteScripts: string[];
  prodScripts: string[];
  prodPreloads: string[];
  translated: boolean;
  adblock: AdblockSignals;
};

type AdblockSignals = {
  // Wie viele /assets/* Ressourcen wurden tatsächlich vom Browser geladen
  // (via Resource-Timing-API)?
  resourceTimingAssets: number;
  // Wie viele /assets/* haben transferSize 0 (== nicht geliefert / geblockt)?
  resourceTimingBlocked: number;
  // Aufschlüsselung nach Origin: same-origin App-Ressourcen sind kritisch,
  // 3rd-party (Analytics/Tracker) sind nur informativ.
  sameOriginResources: number;
  sameOriginBlocked: number;
  thirdPartyResources: number;
  thirdPartyBlocked: number;
  // Wie viele eindeutige Origins lieferten 0 Bytes? Hoher Wert → starker
  // Tracking-Blocker, niedriger Wert + same-origin blocked → AdBlock auf uns.
  blockedOrigins: string[];
  // Ergebnis des Bait-Fetches auf /ads/ads.js — "blocked" deutet stark auf
  // einen AdBlocker hin, "ok" und "pending" sind unauffällig.
  baitFetch: "pending" | "ok" | "blocked" | "skipped";
  // Element mit AdBlock-Honigtopf-Klassen wurde versteckt (display:none /
  // offsetParent null) → Element-Hiding-Filter aktiv.
  baitElementHidden: boolean;
  // Parallele HEAD-Probes auf bekannte Tracker-Domains. Wenn alle
  // fehlschlagen → Tracking-Blocker aktiv (z. B. Brave Shields, uBlock).
  trackerProbes: Record<string, "pending" | "ok" | "blocked">;
  // Browser-Fingerprint zur Kalibrierung. Brave blockt by default,
  // Firefox mit ETP ebenfalls — das ist für 3rd-party erwartbar.
  browser: "brave" | "firefox" | "edge" | "chrome" | "safari" | "unknown";
  // Gewichteter Confidence-Score 0-100, autokalibriert aus allen Signalen.
  // < 30 → keine Aussage, 30-59 → wahrscheinlich Tracker-Blocker (3rd-party),
  // >= 60 → AdBlocker greift in App-Ressourcen ein (kritisch).
  confidence: number;
  confidenceBreakdown: Array<{ signal: string; weight: number }>;
  // Klassifizierung jeder beobachteten Ressource. Erlaubt es im Debug-
  // Panel transparent zu zeigen, WARUM eine URL (nicht) gezählt wurde.
  classified: ClassifiedResource[];
};

export type ClassifiedResource = {
  url: string;
  origin: "same" | "third-party" | "unknown";
  category: "app-bundle" | "synthetic-probe" | "non-critical" | "other";
  blocked: boolean;
  // Erklärt, warum diese URL ausgeschlossen wurde (oder doch zählte).
  reason: string;
};

const EMPTY_ADBLOCK: AdblockSignals = {
  resourceTimingAssets: 0,
  resourceTimingBlocked: 0,
  sameOriginResources: 0,
  sameOriginBlocked: 0,
  thirdPartyResources: 0,
  thirdPartyBlocked: 0,
  blockedOrigins: [],
  baitFetch: "pending",
  baitElementHidden: false,
  trackerProbes: {},
  browser: "unknown",
  confidence: 0,
  confidenceBreakdown: [],
  classified: [],
};

/**
 * Berechnet den Confidence-Score eines AdblockSignals-Snapshots neu — z. B.
 * nachdem ein asynchrones Signal (Bait-Fetch, Tracker-Probes) eingetroffen
 * ist. `extra` erlaubt es, zusätzliche Beiträge einzubringen, die nicht aus
 * dem Snapshot selbst ableitbar sind.
 */
function recalcConfidence(
  a: AdblockSignals,
  extra: Array<{ signal: string; weight: number }> = [],
): AdblockSignals {
  const breakdown: Array<{ signal: string; weight: number }> = [];
  const add = (signal: string, weight: number) => {
    if (weight !== 0) breakdown.push({ signal, weight });
  };
  if (a.resourceTimingBlocked > 0) {
    const ratio = a.resourceTimingBlocked / Math.max(1, a.resourceTimingAssets);
    add(
      `/assets/ blocked ${a.resourceTimingBlocked}/${a.resourceTimingAssets}`,
      Math.round(50 + 30 * ratio),
    );
  } else if (a.sameOriginBlocked > 0) {
    add(
      `same-origin 0/0 (non-bundle) ${a.sameOriginBlocked}`,
      Math.min(15, a.sameOriginBlocked * 5),
    );
  }
  if (a.thirdPartyBlocked > 0) {
    const base = a.browser === "brave" || a.browser === "firefox" ? 3 : 8;
    const cap = a.browser === "brave" || a.browser === "firefox" ? 10 : 25;
    add(`3rd-party blocked ${a.thirdPartyBlocked}`, Math.min(cap, base * a.thirdPartyBlocked));
  }
  if (a.baitElementHidden) add("bait element hidden", 10);
  if (a.browser === "brave") add("Brave browser", 5);
  if (a.baitFetch === "blocked") add("bait fetch blocked", 15);
  for (const e of extra) add(e.signal, e.weight);
  const confidence = Math.min(
    100,
    breakdown.reduce((s, b) => s + b.weight, 0),
  );
  return { ...a, confidence, confidenceBreakdown: breakdown };
}

function isDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("diag") === "1") return true;
    return window.localStorage?.getItem("diag") === "1";
  } catch {
    return false;
  }
}

const SESSION_KEY = "diag:banner";
const LOCAL_KEY = "diag:banner:local";
const ACCOUNT_KEY_PREFIX = "diag:banner:account:";

function readBannerSessionPref(): "on" | "off" {
  if (typeof window === "undefined") return "on";
  try {
    return window.sessionStorage?.getItem(SESSION_KEY) === "off" ? "off" : "on";
  } catch {
    return "on";
  }
}

function writeBannerSessionPref(pref: "on" | "off") {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(SESSION_KEY, pref);
  } catch {
    /* sessionStorage nicht verfügbar */
  }
}

function readLocalPref(): "on" | "off" {
  if (typeof window === "undefined") return "on";
  try {
    return window.localStorage?.getItem(LOCAL_KEY) === "off" ? "off" : "on";
  } catch {
    return "on";
  }
}

function writeLocalPref(pref: "on" | "off") {
  if (typeof window === "undefined") return;
  try {
    if (pref === "off") window.localStorage?.setItem(LOCAL_KEY, "off");
    else window.localStorage?.removeItem(LOCAL_KEY);
  } catch {
    /* localStorage nicht verfügbar */
  }
}

function readAccountPref(userId: string | null): "on" | "off" {
  if (!userId || typeof window === "undefined") return "on";
  try {
    return window.localStorage?.getItem(ACCOUNT_KEY_PREFIX + userId) === "off" ? "off" : "on";
  } catch {
    return "on";
  }
}

function writeAccountPref(userId: string | null, pref: "on" | "off") {
  if (!userId || typeof window === "undefined") return;
  try {
    if (pref === "off") window.localStorage?.setItem(ACCOUNT_KEY_PREFIX + userId, "off");
    else window.localStorage?.removeItem(ACCOUNT_KEY_PREFIX + userId);
  } catch {
    /* localStorage nicht verfügbar */
  }
}

/**
 * Erkennt clientseitige Render-Probleme (geblockte CSS/JS-Bundles,
 * aktive Seitenübersetzung, Brave-Shields, AdBlocker) und zeigt eine
 * Diagnosebox mit möglichen Ursachen. Nutzt ausschließlich Inline-Styles,
 * damit die Box auch dann sichtbar ist, wenn Tailwind/CSS geblockt wurde.
 */
export function DiagnosticBanner() {
  const [issue, setIssue] = useState<Issue | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "ok" | "err">("idle");
  const [signals, setSignals] = useState<Signals | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugVisible, setDebugVisible] = useState(false);
  // SSR-sicher: deterministischer Startwert "on", echte Präferenz wird unten im
  // useEffect aus session/localStorage nachgezogen. Verhindert Hydration-Mismatch.
  const [bannerPref, setBannerPref] = useState<"on" | "off">("on");
  const [localPref, setLocalPref] = useState<"on" | "off">("on");
  // User-ID aus dem zentralen AuthProvider statt eines eigenen
  // onAuthStateChange-Subscribers (würde sonst zusätzlich zum Provider feuern
  // und Render-Flackern verursachen — siehe tanstack-supabase-integration).
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [accountPref, setAccountPref] = useState<"on" | "off">("on");

  const setBannerForSession = (pref: "on" | "off") => {
    writeBannerSessionPref(pref);
    setBannerPref(pref);
  };

  const setBannerForLocal = (pref: "on" | "off") => {
    writeLocalPref(pref);
    setLocalPref(pref);
  };

  const setBannerForAccount = (pref: "on" | "off") => {
    writeAccountPref(userId, pref);
    setAccountPref(pref);
  };

  const resetBannerPrefs = () => {
    writeBannerSessionPref("on");
    writeLocalPref("on");
    writeAccountPref(userId, "on");
    setBannerPref("on");
    setLocalPref("on");
    setAccountPref("on");
  };

  // Effektive Sichtbarkeit: jede aktive "off"-Quelle blendet den Banner aus.
  const effectivePref: "on" | "off" =
    bannerPref === "off" || localPref === "off" || accountPref === "off" ? "off" : "on";

  // Echte Präferenzen erst client-seitig nachziehen (vermeidet SSR/Hydration-Mismatch).
  useEffect(() => {
    setBannerPref(readBannerSessionPref());
    setLocalPref(readLocalPref());
  }, []);

  // Account-Präferenz neu laden, wenn sich der eingeloggte User ändert.
  useEffect(() => {
    setAccountPref(readAccountPref(userId));
  }, [userId]);

  async function copyDebugInfo() {
    if (!issue) return;
    const lines = [
      "=== Diagnostic Banner Debug-Info ===",
      "Zeitpunkt: " + new Date().toISOString(),
      "Seite: " + window.location.href,
      "User-Agent: " + (navigator.userAgent || "n/a"),
      "navigator.onLine: " + (navigator.onLine ? "true" : "false"),
      "Diagnose: " + issue.title,
      "Schweregrad: " + (issue.severe ? "severe" : "info"),
      "Mögliche Ursachen:",
      ...issue.causes.map((c) => "  - " + c),
    ];
    if (issue.steps && issue.steps.length) {
      lines.push("Empfohlene Schritte:");
      issue.steps.forEach((s) => lines.push("  - " + s));
    }
    const text = lines.join("\n");
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand?.("copy") ?? false;
        document.body.removeChild(ta);
      }
    } catch {
      ok = false;
    }
    setCopyState(ok ? "ok" : "err");
    window.setTimeout(() => setCopyState("idle"), 1800);
  }

  useEffect(() => {
    // Kurz warten, damit alle <link>-Stylesheets versucht wurden zu laden.
    const t = window.setTimeout(() => {
      const problems: string[] = [];
      const causes = new Set<string>();
      let cssMissing = false;

      // ---------------------------------------------------------------
      // Build-Modus erkennen: Vite-Dev vs. Production-Bundle.
      //   - Dev:  Module werden live aus /src/ und /@vite/ geladen,
      //           CSS wird per JS als <style>-Tag injiziert.
      //   - Prod: Es gibt gebundlete /assets/*.js + /assets/*.css Dateien.
      // ---------------------------------------------------------------
      const allScripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"));
      const allLinks = Array.from(document.querySelectorAll<HTMLLinkElement>("link[href]"));
      const hasViteDevMarker = allScripts.some((s) =>
        /\/@vite\/client|\/@react-refresh|\/src\//.test(s.src),
      );
      const hasProdAssetJs = allScripts.some((s) => /\/assets\/.*\.js/.test(s.src));
      const hasProdAssetCss = allLinks.some(
        (l) => l.rel === "stylesheet" && /\/assets\/.*\.css/.test(l.href),
      );
      const hasProdPreload = allLinks.some(
        (l) => l.rel === "modulepreload" && /\/assets\/.*\.js/.test(l.href),
      );
      const isDev = hasViteDevMarker && !hasProdAssetJs;
      const isProd = hasProdAssetJs || hasProdAssetCss || hasProdPreload;

      // ---------------------------------------------------------------
      // 1) CSS-Sentinel: prüfen ob *irgendein* Stylesheet (Tailwind oder
      //    Vite-Dev <style>) tatsächlich greift. Wir testen mehrere
      //    Token-Klassen, damit das Ergebnis nicht von einer einzelnen
      //    Utility-Klasse abhängt.
      // ---------------------------------------------------------------
      // Drei-Sentinel-Heuristik in ausgelagertem Helper (unit-getestet
      // in src/lib/-css-sentinel.test.ts, deckt das Tailwind-Purging-
      // False-Positive-Szenario explizit ab).
      const sentinel = runCssSentinel({
        doc: document,
        getComputedStyle: (el) => window.getComputedStyle(el),
      });
      const { hasDesignTokens, hiddenWorks, loadedSheets } = sentinel;
      if (sentinel.cssMissing) {
        cssMissing = true;
        problems.push("App-Stylesheet nicht angewandt");
        if (isProd) {
          causes.add("AdBlocker / Brave Shields blockiert /assets/*.css");
          causes.add("Browser-Cache liefert eine kaputte Version → Strg+Shift+R");
        } else {
          causes.add("Vite-Dev-Server konnte CSS nicht injizieren");
          causes.add("Eine Browser-Extension entfernt injizierte <style>-Tags");
        }
      }

      // ---------------------------------------------------------------
      // 4) Snapshot der erkannten Signale für das Debug-Panel & Logging.
      // ---------------------------------------------------------------
      const stylesheetLinks = allLinks.filter((l) => l.rel === "stylesheet").length;
      const snapshot: Signals = {
        capturedAt: new Date().toISOString(),
        buildMode: isDev ? "dev" : isProd ? "prod" : "unknown",
        hasViteDevMarker,
        hasProdAssetJs,
        hasProdAssetCss,
        hasProdPreload,
        hiddenWorks,
        loadedSheets,
        hasDesignTokens,
        stylesheetLinks,
        scriptCount: allScripts.length,
        viteScripts: allScripts
          .map((s) => s.src)
          .filter((src) => /\/@vite\/|\/@react-refresh|\/src\//.test(src))
          .slice(0, 10),
        prodScripts: allScripts
          .map((s) => s.src)
          .filter((src) => /\/assets\/.*\.js/.test(src))
          .slice(0, 10),
        prodPreloads: allLinks
          .filter((l) => l.rel === "modulepreload" && /\/assets\/.*\.js/.test(l.href))
          .map((l) => l.href)
          .slice(0, 10),
        translated: false, // wird unten gesetzt
        adblock: { ...EMPTY_ADBLOCK },
      };

      // ---------------------------------------------------------------
      // 2) AdBlock / Tracking-Blocker erkennen — kombinierte Indikatoren.
      //    a) Resource-Timing: gehören erwartete /assets/* zu den geladenen?
      //    b) Bait-Element: wird ein .adsbox-Köderelement vom Filter versteckt?
      //    c) Bait-Fetch (async, weiter unten): wird /ads/ads.js geblockt?
      // ---------------------------------------------------------------
      const adblock: AdblockSignals = { ...EMPTY_ADBLOCK };
      const sameOrigin = typeof window !== "undefined" ? window.location.origin : "";
      const blockedOriginSet = new Set<string>();
      try {
        const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
        // URLs, die wir selbst nur zu Diagnose-Zwecken anstoßen — sie
        // schlagen häufig fehl (Bait!) und dürfen NIE als App-Blockade
        // zählen, sonst erzeugt der Banner sich selbst.
        const isSyntheticProbe = (url: string) =>
          /\/ads\/ads\.js/.test(url) ||
          /\/__lovable/.test(url) ||
          /googletagmanager\.com|google-analytics\.com|doubleclick\.net|facebook\.net/.test(url);

        // Pfade, die nicht App-kritisch sind — ihre Blockade bricht die
        // App nicht: Favicons, Manifests, Sourcemaps, robots.txt etc.
        const isNonCritical = (url: string) =>
          /\.map(\?|$)/.test(url) ||
          /\/favicon\.|\/apple-touch-icon|\.webmanifest|\/robots\.txt|\/sitemap/.test(url);

        // Heuristik für "geblockt": transferSize 0 UND decodedBodySize 0
        // UND duration < 5ms (echte Cache-Hits haben decodedBodySize > 0;
        // echte Netz-Antworten brauchen Zeit — 0/0/<5ms ist typisch für
        // Extension-Cancel via `chrome.webRequest.onBeforeRequest`).
        // Synthetische Probes und non-critical Ressourcen werden ausgeschlossen.
        const isBlocked = (e: PerformanceResourceTiming) =>
          e.transferSize === 0 &&
          e.decodedBodySize === 0 &&
          e.duration < 5 &&
          e.initiatorType !== "beacon" &&
          e.initiatorType !== "xmlhttprequest" && // HEAD-Probes aus Self-Test
          !isSyntheticProbe(e.name) &&
          !isNonCritical(e.name);

        // App-Bundles: JS/CSS/Font unter /assets/
        const isAppBundle = (url: string) => /\/assets\/.+\.(js|css|woff2?|ttf)(\?|$)/.test(url);
        const assetEntries = entries.filter((e) => isAppBundle(e.name));
        adblock.resourceTimingAssets = assetEntries.length;
        adblock.resourceTimingBlocked = assetEntries.filter(isBlocked).length;

        // Per-Origin-Aufschlüsselung + transparente Klassifizierung jeder
        // beobachteten Ressource. Damit kann das Debug-Panel zeigen,
        // WARUM eine URL (nicht) in den Score eingegangen ist.
        const classified: ClassifiedResource[] = [];
        for (const e of entries) {
          const url = e.name;
          let origin: ClassifiedResource["origin"] = "unknown";
          let originStr = "";
          try {
            originStr = new URL(url).origin;
            origin = originStr === sameOrigin ? "same" : "third-party";
          } catch {
            // pass — origin bleibt "unknown"
          }

          // Block-Status nach den gleichen Roh-Kriterien (ohne Filter)
          const rawBlocked =
            e.transferSize === 0 &&
            e.decodedBodySize === 0 &&
            e.duration < 5 &&
            e.initiatorType !== "beacon" &&
            e.initiatorType !== "xmlhttprequest";

          let category: ClassifiedResource["category"];
          let reason: string;
          if (isSyntheticProbe(url)) {
            category = "synthetic-probe";
            reason = "Eigener Diagnose-Request (Bait / Tracker-Probe / __lovable) — ignoriert";
          } else if (isNonCritical(url)) {
            category = "non-critical";
            reason = "Favicon / Sourcemap / Manifest / robots — App-irrelevant, ignoriert";
          } else if (isAppBundle(url)) {
            category = "app-bundle";
            reason = rawBlocked
              ? "App-Bundle — als geblockt gezählt (kritisch)"
              : "App-Bundle — erfolgreich geladen";
          } else {
            category = "other";
            reason = rawBlocked
              ? "Sonstige Ressource 0/0 — nur schwaches Hinweissignal"
              : "Sonstige Ressource — geladen";
          }

          // In die Origin-Zähler fließen nur Ressourcen ein, die nicht
          // unsere eigenen Probes sind.
          if (category !== "synthetic-probe" && origin !== "unknown") {
            if (origin === "same") {
              adblock.sameOriginResources++;
              if (isBlocked(e)) {
                adblock.sameOriginBlocked++;
                blockedOriginSet.add(originStr);
              }
            } else {
              adblock.thirdPartyResources++;
              if (isBlocked(e)) {
                adblock.thirdPartyBlocked++;
                blockedOriginSet.add(originStr);
              }
            }
          }

          classified.push({
            url,
            origin,
            category,
            blocked: rawBlocked,
            reason,
          });
        }
        adblock.classified = classified;
        adblock.blockedOrigins = Array.from(blockedOriginSet).slice(0, 20);
      } catch {
        /* Performance-API nicht verfügbar */
      }

      // -----------------------------------------------------------------
      // Browser-Fingerprint zur Kalibrierung.
      // -----------------------------------------------------------------
      const ua = navigator.userAgent;
      const navWithBrave = navigator as unknown as {
        brave?: { isBrave?: () => Promise<boolean> };
      };
      const isBrave = typeof navWithBrave.brave?.isBrave === "function";
      adblock.browser = isBrave
        ? "brave"
        : /Edg\//.test(ua)
          ? "edge"
          : /Firefox\//.test(ua)
            ? "firefox"
            : /Chrome\//.test(ua)
              ? "chrome"
              : /Safari\//.test(ua)
                ? "safari"
                : "unknown";

      // Bait-Element: typische AdBlock-Selektoren versteckt?
      const bait = document.createElement("div");
      bait.className = "adsbox ad-banner ad-placement";
      bait.style.cssText = "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;";
      bait.innerHTML = "&nbsp;";
      document.body.appendChild(bait);
      const baitStyle = getComputedStyle(bait);
      adblock.baitElementHidden =
        baitStyle.display === "none" ||
        baitStyle.visibility === "hidden" ||
        bait.offsetParent === null ||
        bait.offsetHeight === 0;
      document.body.removeChild(bait);

      // -----------------------------------------------------------------
      // Confidence-Score: gewichtete Summe der Signale, kalibriert nach
      // Browser (Brave/Firefox blocken 3rd-party by default → weniger Gewicht).
      // -----------------------------------------------------------------
      const breakdown: Array<{ signal: string; weight: number }> = [];
      const add = (signal: string, weight: number) => {
        if (weight !== 0) breakdown.push({ signal, weight });
      };

      // KRITISCH: same-origin App-Ressourcen geblockt → User sieht kaputte App
      // Hohes Gewicht NUR, wenn echte App-Bundles unter /assets/ geblockt
      // wurden (resourceTimingBlocked). Sonstiges same-origin 0/0 ist nur
      // ein schwaches Hinweis-Signal (+10).
      if (adblock.resourceTimingBlocked > 0) {
        const ratio = adblock.resourceTimingBlocked / Math.max(1, adblock.resourceTimingAssets);
        add(
          `/assets/ blocked ${adblock.resourceTimingBlocked}/${adblock.resourceTimingAssets}`,
          Math.round(50 + 30 * ratio),
        );
      } else if (adblock.sameOriginBlocked > 0) {
        add(
          `same-origin 0/0 (non-bundle) ${adblock.sameOriginBlocked}`,
          Math.min(15, adblock.sameOriginBlocked * 5),
        );
      }

      // 3rd-party blocked: in Brave/Firefox normal, sonst Hinweis
      if (adblock.thirdPartyBlocked > 0) {
        const base = adblock.browser === "brave" || adblock.browser === "firefox" ? 3 : 8;
        const cap = adblock.browser === "brave" || adblock.browser === "firefox" ? 10 : 25;
        add(
          `3rd-party blocked ${adblock.thirdPartyBlocked}`,
          Math.min(cap, base * adblock.thirdPartyBlocked),
        );
      }

      // Bait-Element versteckt: Element-Hiding-Filter aktiv
      if (adblock.baitElementHidden) {
        add("bait element hidden", 10);
      }

      // Brave/Firefox per se: leichter Anstieg (höhere Wahrscheinlichkeit,
      // dass Tracking-Schutz aktiv ist, aber ohne weitere Signale nicht relevant)
      if (isBrave) add("Brave browser", 5);

      // Bait-Fetch wird async unten ergänzt — falls schon bekannt (re-render),
      // zähle ihn jetzt schon.
      if (adblock.baitFetch === "blocked") add("bait fetch blocked", 15);

      adblock.confidence = Math.min(
        100,
        breakdown.reduce((sum, b) => sum + b.weight, 0),
      );
      adblock.confidenceBreakdown = breakdown;

      // -----------------------------------------------------------------
      // Auslöse-Entscheidung — autokalibriert über Confidence + Same-Origin.
      // Nur wenn same-origin App-Ressourcen tatsächlich fehlen, melden wir
      // ein Problem. Reines 3rd-party-Blocking → Debug-Panel, kein Banner.
      // -----------------------------------------------------------------
      const appBroken =
        // Strikt: nur wenn echte App-Bundles unter /assets/ fehlen.
        // Anderes same-origin 0/0 (HEAD-Probes, Beacons …) zählt nicht.
        isProd && adblock.resourceTimingBlocked > 0 && adblock.confidence >= 50;

      if (appBroken) {
        problems.push(
          `AdBlocker blockiert App-Bundles (${adblock.resourceTimingBlocked}/${adblock.resourceTimingAssets}, confidence ${adblock.confidence})`,
        );
        causes.add(
          "AdBlocker oder Privacy-Extension (uBlock, AdBlock, Ghostery, Brave Shields) blockiert eigene App-Ressourcen",
        );
        causes.add("Lösung: für diese Domain deaktivieren oder zur Whitelist hinzufügen");
      } else if (adblock.confidence >= 30 && adblock.thirdPartyBlocked > 0) {
        // Informativ: Tracking-Blocker aktiv, App läuft aber.

        console.info(
          `[DiagnosticBanner] Tracking-Blocker erkannt (confidence ${adblock.confidence}, ${adblock.thirdPartyBlocked} 3rd-party origins geblockt) — App funktioniert.`,
        );
      }

      // ---------------------------------------------------------------
      // 3) JS-Bundles prüfen — separate Logik für Dev und Prod.
      // ---------------------------------------------------------------
      // Hinweis: Wir prüfen JS-Bundles bewusst NICHT mehr über das
      // Vorhandensein von <script src="/assets/*.js">-Tags. Bei Vite/
      // TanStack-Production-Builds kann der Entry inline gerendert
      // werden oder über modulepreload geladen werden, ohne dass ein
      // separates <script>-Tag im DOM erscheint. Da dieser Banner
      // selbst von React gerendert wird, ist sein bloßes Erscheinen
      // bereits der Beweis, dass die JS-Bundles geladen wurden — ein
      // DOM-basierter Check würde hier nur False Positives erzeugen.
      // Echte Bundle-Blockaden erkennen wir oben über Resource-Timing
      // (prodAssetsBlocked / prodAssetsSuspiciouslyLow).
      void hasProdAssetJs;
      void hasProdPreload;

      // 4) Aktive Browser-Übersetzung (Google/Brave/Edge fügen Klassen am <html> hinzu)
      const htmlEl = document.documentElement;
      const translated =
        htmlEl.classList.contains("translated-ltr") ||
        htmlEl.classList.contains("translated-rtl") ||
        htmlEl.getAttribute("translate") === "yes" ||
        document.querySelector("html[class*='translated']") !== null ||
        document.querySelector("font[_msttexthash]") !== null;
      if (translated) {
        problems.push("Seite wird vom Browser übersetzt");
        causes.add(
          "Browser-Übersetzung (Brave/Chrome/Edge) zerlegt das DOM → 'Original anzeigen' klicken",
        );
      }
      snapshot.translated = translated;
      snapshot.adblock = adblock;
      setSignals(snapshot);
      const debugFlag = isDebugEnabled();
      setDebugVisible(debugFlag);
      // Immer in die Konsole loggen, damit man auch ohne ?diag=1 nachsehen kann.

      console.info("[DiagnosticBanner] signals", snapshot, {
        problems,
        causes: Array.from(causes),
      });

      // 5) Brave-Hinweis (Detection bereits oben gemacht)
      if (problems.length > 0 && isBrave) {
        causes.add(
          "Brave erkannt: prüfe das Löwen-Icon in der Adressleiste → Shields für diese Seite deaktivieren",
        );
      }

      if (problems.length > 0) {
        const steps = cssMissing
          ? [
              "Klicke in der Adressleiste auf das Schutzschild-Icon (Brave-Löwe, uBlock, AdBlock) und deaktiviere es für diese Seite, dann neu laden.",
              "Wenn die Seite automatisch übersetzt wurde, klicke auf das Übersetzungs-Symbol und wähle 'Original anzeigen'.",
              "Erzwinge ein hartes Neuladen mit Strg+Shift+R (Windows/Linux) bzw. Cmd+Shift+R (Mac).",
              "Öffne die Seite testweise im Inkognito-Fenster oder einem anderen Browser. Funktioniert sie dort, ist eine Extension die Ursache.",
              "Falls weiterhin defekt: alle Browser-Extensions deaktivieren und erneut laden.",
            ]
          : [];
        setIssue({
          title: problems.join(" · "),
          causes: Array.from(causes),
          severe: cssMissing,
          steps,
        });
      }

      // ---------------------------------------------------------------
      // Async Bait-Fetch: prüfen, ob /ads/ads.js vom Browser/Extension
      // geblockt wird. Läuft NACH dem Setup, weil es eine Roundtrip kostet.
      // ---------------------------------------------------------------
      void (async () => {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 2500);
        let result: AdblockSignals["baitFetch"] = "pending";
        try {
          const res = await fetch("/ads/ads.js", {
            method: "HEAD",
            mode: "no-cors",
            cache: "no-store",
            signal: controller.signal,
          });
          // no-cors: opaque response. Wenn wir hier landen, kam *irgendetwas*
          // zurück → nicht netzwerk-geblockt. (404 vom Server zählt als "ok".)
          result = res.type === "opaque" || res.ok ? "ok" : "ok";
        } catch {
          // Netzwerk-Fehler / from extension → fast immer AdBlock.
          result = "blocked";
        } finally {
          window.clearTimeout(timeoutId);
        }
        setSignals((prev) =>
          prev
            ? {
                ...prev,
                adblock: recalcConfidence({
                  ...prev.adblock,
                  baitFetch: result,
                }),
              }
            : prev,
        );
        // Wenn der Bait klar geblockt ist, aber wir noch kein Problem
        // gemeldet haben (App lief trotzdem durch) → still bleiben.
        // Nur wenn parallel auch echte App-Symptome existieren, ergänzen
        // wir die Ursache.
        if (result === "blocked") {
          console.info("[DiagnosticBanner] AdBlocker erkannt (bait /ads/ads.js geblockt)");
          setIssue((prev) => {
            if (!prev) return prev;
            const extra = "AdBlocker bestätigt (Bait-Fetch /ads/ads.js geblockt)";
            if (prev.causes.includes(extra)) return prev;
            return { ...prev, causes: [...prev.causes, extra] };
          });
        }
      })();

      // ---------------------------------------------------------------
      // Parallele Tracker-Probes auf bekannte Domains. Wenn ALLE geblockt
      // sind, ist ein generischer Tracking-Blocker aktiv (≠ AdBlocker auf
      // unsere Assets). Wir nutzen no-cors HEAD-Probes mit kurzer Timeout.
      // ---------------------------------------------------------------
      const trackerUrls = [
        "https://www.googletagmanager.com/gtag/js?id=GTM-DIAG",
        "https://www.google-analytics.com/analytics.js",
        "https://stats.g.doubleclick.net/dc.js",
        "https://connect.facebook.net/en_US/fbevents.js",
      ];
      Promise.all(
        trackerUrls.map(async (url) => {
          const ctrl = new AbortController();
          const tid = window.setTimeout(() => ctrl.abort(), 2500);
          try {
            await fetch(url, {
              method: "HEAD",
              mode: "no-cors",
              cache: "no-store",
              signal: ctrl.signal,
            });
            return [url, "ok"] as const;
          } catch {
            return [url, "blocked"] as const;
          } finally {
            window.clearTimeout(tid);
          }
        }),
      ).then((results) => {
        const probes: Record<string, "pending" | "ok" | "blocked"> = {};
        for (const [url, state] of results) probes[url] = state;
        const blockedCount = results.filter(([, s]) => s === "blocked").length;
        setSignals((prev) => {
          if (!prev) return prev;
          const next: AdblockSignals = {
            ...prev.adblock,
            trackerProbes: probes,
          };
          // Zusätzlicher Confidence-Beitrag aus Probe-Ergebnis (kalibriert)
          const browser = prev.adblock.browser;
          const probeWeight =
            browser === "brave" || browser === "firefox"
              ? blockedCount * 2 // erwartbar, niedriges Gewicht
              : blockedCount * 6;
          const recalculated = recalcConfidence(next, [
            { signal: `tracker probes blocked ${blockedCount}/4`, weight: probeWeight },
          ]);
          return { ...prev, adblock: recalculated };
        });

        console.info(`[DiagnosticBanner] tracker probes: ${blockedCount}/4 blocked`, probes);
      });

      // ---------------------------------------------------------------
      // Aktiver Asset-Selbsttest — stärkstes Einzelsignal, da er das
      // tatsächliche Browser-Verhalten gegen unsere echten Bundles misst.
      // Ergebnis dominiert die heuristischen Signale:
      //   • allFailed && totalCount ≥ 2 → harte Bestätigung (+50 confidence,
      //     Issue erzwingen falls nicht vorhanden).
      //   • alles ok && totalCount ≥ 1  → harte Widerlegung: ein evtl.
      //     gemeldetes "App-Bundles blockiert"-Issue wird zurückgenommen
      //     (Resource-Timing kann durch Cache/Preload trügen).
      // ---------------------------------------------------------------
      void (async () => {
        const selfTest = await runAssetSelfTest();

        console.info("[DiagnosticBanner] asset self-test", selfTest);
        if (selfTest.totalCount === 0) return;

        // Im Dev-Modus prüft der Self-Test /@vite/client + /src/main.tsx.
        // Wenn der Vite-HMR-Socket kurz weg ist (Server-Restart, Netzwerk-
        // Reconnect), schlagen beide Probes fehl — das ist KEIN AdBlocker
        // und darf NIEMALS das Severe-Overlay auslösen. Wir akzeptieren
        // hardBlock daher nur im Production-Modus, wo die Probes echte
        // /assets/*.{js,css} Bundles testen.
        const hardBlock =
          selfTest.allFailed && selfTest.totalCount >= 2 && selfTest.mode === "prod";
        const hardPass = selfTest.failedCount === 0 && selfTest.totalCount >= 1;

        setSignals((prev) => {
          if (!prev) return prev;
          const extra: Array<{ signal: string; weight: number }> = [];
          if (hardBlock) {
            extra.push({
              signal: `self-test allFailed ${selfTest.failedCount}/${selfTest.totalCount}`,
              weight: 50,
            });
          } else if (hardPass) {
            extra.push({
              signal: `self-test ok ${selfTest.totalCount}/${selfTest.totalCount}`,
              weight: -60,
            });
          }
          return {
            ...prev,
            adblock: recalcConfidence(prev.adblock, extra),
          };
        });

        if (hardPass) {
          // Widerlegung: false-positive zurücknehmen, wenn das Issue
          // ausschließlich auf "App-Bundles blockiert" basiert.
          setIssue((prev) => {
            if (!prev) return prev;
            const isAppBundleClaim =
              /App-Bundles?/.test(prev.title) ||
              prev.causes.some((c) => /App-Ressourcen|App-Bundles?/.test(c));
            if (!isAppBundleClaim) return prev;

            console.info("[DiagnosticBanner] self-test ok → ziehe AdBlock-Befund zurück");
            return null;
          });
        } else if (hardBlock) {
          setIssue((prev) => {
            const cause =
              "AdBlocker/Netzwerk blockiert App-Bundles — Selbsttest bestätigt (alle Probes fehlgeschlagen)";
            if (!prev) {
              return {
                title: `App-Bundles blockiert (Selbsttest ${selfTest.failedCount}/${selfTest.totalCount})`,
                causes: [cause],
                severe: true,
                steps: [
                  "AdBlocker / Privacy-Extension für diese Domain deaktivieren und neu laden.",
                  "Im Inkognito-Fenster öffnen, um Extensions auszuschließen.",
                ],
              };
            }
            if (prev.causes.includes(cause)) return prev;
            return { ...prev, causes: [...prev.causes, cause] };
          });
        }

        // ---------------------------------------------------------
        // Anonyme Telemetrie (einmal pro Session). Wir nutzen einen
        // funktionalen setIssue-Aufruf nur zum Auslesen des aktuellen
        // Werts, ohne ihn zu verändern — so erwischen wir die finale
        // Banner-Entscheidung NACH allen Self-Test-Korrekturen.
        // ---------------------------------------------------------
        setIssue((current) => {
          sendDiagnosticTelemetry({
            bannerTriggered: current !== null,
            cssMissing:
              !snapshot.hasDesignTokens && !snapshot.hiddenWorks && snapshot.loadedSheets === 0,
            hasDesignTokens: snapshot.hasDesignTokens,
            hiddenWorks: snapshot.hiddenWorks,
            loadedSheets: snapshot.loadedSheets,
            buildMode: snapshot.buildMode,
            browserFamily: snapshot.adblock.browser,
            selfTestTotal: selfTest.totalCount,
            selfTestFailed: selfTest.failedCount,
            translated: snapshot.translated,
          });
          return current;
        });
      })();
    }, 800);

    return () => window.clearTimeout(t);
  }, []);

  // Debug-Panel als zweite Render-Spur (unabhängig vom Issue-Banner).
  const debugPanel =
    debugVisible && signals ? (
      <DebugPanel
        signals={signals}
        open={debugOpen}
        onToggle={() => setDebugOpen((v) => !v)}
        onDismiss={() => setDebugVisible(false)}
        bannerPref={bannerPref}
        onSetBannerPref={setBannerForSession}
        localPref={localPref}
        onSetLocalPref={setBannerForLocal}
        accountPref={accountPref}
        onSetAccountPref={setBannerForAccount}
        hasUser={userId !== null}
        onResetPrefs={resetBannerPrefs}
      />
    ) : null;

  // Kleine Pille zum Wiedereinblenden, wenn der Banner für diese Session
  // ausgeblendet wurde — aber nur dann sichtbar, wenn aktuell tatsächlich
  // ein Problem erkannt wurde (sonst gibt's nichts wiedereinzublenden).
  const restorePill =
    effectivePref === "off" && issue ? (
      <button
        type="button"
        onClick={resetBannerPrefs}
        title="Diagnose-Banner wieder einblenden"
        style={{
          position: "fixed",
          bottom: 12,
          left: 12,
          zIndex: 2147483646,
          padding: "6px 10px",
          background: "#fef3c7",
          color: "#92400e",
          border: "1px solid #f59e0b",
          borderRadius: 999,
          cursor: "pointer",
          fontSize: 11,
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        ⚠ Diagnose-Banner einblenden
      </button>
    ) : null;

  // Banner für diese Session ausgeblendet → nur Debug-Panel + Pille zeigen.
  if (!issue || effectivePref === "off") {
    return (
      <>
        {debugPanel}
        {restorePill}
      </>
    );
  }

  // Schweres Problem (kein CSS) → Vollbild-Overlay mit Reparaturschritten
  if (issue.severe) {
    return (
      <>
        {debugPanel}
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="diag-overlay-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483647,
            background: "rgba(15,23,42,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
          }}
        >
          <div
            style={{
              maxWidth: 600,
              width: "100%",
              background: "#ffffff",
              color: "#0f172a",
              border: "1px solid #fb923c",
              borderRadius: 12,
              padding: 24,
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <div id="diag-overlay-title" style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              ⚠️ Diese Seite kann nicht korrekt angezeigt werden
            </div>
            <div style={{ marginBottom: 16, color: "#475569" }}>
              Das App-Stylesheet wurde nicht geladen. Inhalte sind vorhanden, aber werden ohne
              Design dargestellt. Häufig wird das CSS-Bundle von einer Browser-Extension oder
              Schutzfunktion blockiert.
            </div>

            <div style={{ fontWeight: 600, marginBottom: 6 }}>So behebst du es:</div>
            <ol style={{ margin: "0 0 16px", paddingLeft: 22 }}>
              {issue.steps.map((s, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  {s}
                </li>
              ))}
            </ol>

            <details style={{ marginBottom: 16 }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>Technische Details</summary>
              <div style={{ marginTop: 8, color: "#475569" }}>{issue.title}</div>
              <ul style={{ marginTop: 6, paddingLeft: 18, color: "#475569" }}>
                {issue.causes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </details>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  padding: "8px 16px",
                  background: "#0f172a",
                  color: "#fff",
                  border: 0,
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Seite neu laden
              </button>
              <button
                type="button"
                onClick={() => setIssue(null)}
                style={{
                  padding: "8px 16px",
                  background: "transparent",
                  color: "#0f172a",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Trotzdem fortfahren
              </button>
              <button
                type="button"
                onClick={() => setBannerForSession("off")}
                title="Diesen Banner für die aktuelle Browser-Session ausblenden"
                style={{
                  padding: "8px 16px",
                  background: "transparent",
                  color: "#0f172a",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Für diese Session ausblenden
              </button>
              <button
                type="button"
                onClick={copyDebugInfo}
                style={{
                  padding: "8px 16px",
                  background: "transparent",
                  color: "#0f172a",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                {copyState === "ok"
                  ? "✓ Kopiert"
                  : copyState === "err"
                    ? "⚠ Fehlgeschlagen"
                    : "Debug-Info kopieren"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {debugPanel}
      <div
        role="alert"
        style={{
          position: "fixed",
          top: 12,
          left: 12,
          right: 12,
          zIndex: 2147483647,
          maxWidth: 560,
          margin: "0 auto",
          padding: "14px 16px",
          background: "#fff7ed",
          color: "#7c2d12",
          border: "1px solid #fb923c",
          borderRadius: 8,
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
          fontSize: 13,
          lineHeight: 1.45,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>⚠️ Darstellungsproblem erkannt</div>
        <div style={{ marginBottom: 8 }}>{issue.title}</div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Mögliche Ursachen:</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {issue.causes.map((c) => (
            <li key={c} style={{ marginBottom: 2 }}>
              {c}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setIssue(null)}
          style={{
            marginTop: 10,
            padding: "4px 10px",
            background: "#7c2d12",
            color: "#fff",
            border: 0,
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          Schließen
        </button>
        <button
          type="button"
          onClick={() => setBannerForSession("off")}
          title="Diesen Banner für die aktuelle Browser-Session ausblenden"
          style={{
            marginTop: 10,
            marginLeft: 8,
            padding: "4px 10px",
            background: "transparent",
            color: "#7c2d12",
            border: "1px solid #7c2d12",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          Session ausblenden
        </button>
        <button
          type="button"
          onClick={copyDebugInfo}
          style={{
            marginTop: 10,
            marginLeft: 8,
            padding: "4px 10px",
            background: "transparent",
            color: "#7c2d12",
            border: "1px solid #7c2d12",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          {copyState === "ok"
            ? "✓ Kopiert"
            : copyState === "err"
              ? "⚠ Fehlgeschlagen"
              : "Debug-Info kopieren"}
        </button>
      </div>
    </>
  );
}

function downloadSignalsJson(signals: Signals) {
  try {
    const payload = {
      capturedAt: signals.capturedAt,
      exportedAt: new Date().toISOString(),
      page: typeof window !== "undefined" ? window.location.href : "n/a",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "n/a",
      onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
      signals,
    };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = signals.capturedAt.replace(/[:.]/g, "-");
    a.href = url;
    a.download = `diagnostic-signals-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    console.error("[DiagnosticBanner] JSON-Export fehlgeschlagen", err);
  }
}

/**
 * Visualisiert die drei CSS-Sentinels mit Klartext-Erklärung. Hilft beim
 * Debuggen von False Positives (z. B. Tailwind v4 purged `.hidden` aus
 * dem Bundle, obwohl das Stylesheet vollständig geladen wurde).
 * Die UND-Verknüpfung in `runCssSentinel` ist hier farblich gespiegelt:
 *   grün  = Sentinel grün → Stylesheet ist nachweislich da
 *   gelb  = Sentinel rot, aber andere Sentinels kompensieren
 *   rot   = Sentinel rot UND alle anderen rot → CSS gilt als fehlend
 */
function SentinelSection({ signals }: { signals: Signals }) {
  const cssMissing = !signals.hasDesignTokens && !signals.hiddenWorks && signals.loadedSheets === 0;
  const sentinels: Array<{
    label: string;
    ok: boolean;
    value: string;
    explain: string;
  }> = [
    {
      label: "Design-Token (--background)",
      ok: signals.hasDesignTokens,
      value: signals.hasDesignTokens ? "definiert" : "leer / nicht definiert",
      explain:
        "Robustester Sentinel: prüft, ob unsere CSS-Variable aus src/styles.css am :root-Element auflösbar ist. Unabhängig von Tailwind-Purging.",
    },
    {
      label: ".hidden-Utility wirkt",
      ok: signals.hiddenWorks,
      value: signals.hiddenWorks ? "display: none" : "display ≠ none",
      explain:
        "Klassischer Tailwind-Indikator. Kann in Tailwind v4 gepurged sein, wenn die Klasse nirgends in den gescannten Sources vorkommt — das ist KEIN Hinweis auf ein kaputtes Stylesheet.",
    },
    {
      label: "Stylesheets mit Regeln",
      ok: signals.loadedSheets > 0,
      value: String(signals.loadedSheets),
      explain:
        "Zählt alle same-origin Sheets, deren cssRules lesbar sind und mindestens eine Regel enthalten. Cross-origin Sheets (Fonts, CDNs) werfen SecurityError und werden bewusst nicht gezählt.",
    },
  ];

  return (
    <div
      style={{
        marginBottom: 10,
        padding: "8px 10px",
        border: `1px solid ${cssMissing ? "#7f1d1d" : "#334155"}`,
        borderRadius: 6,
        background: cssMissing ? "#450a0a" : "#0b1220",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <strong style={{ color: "#e2e8f0", fontSize: 12 }}>CSS-Sentinels</strong>
        <span
          style={{
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 4,
            background: cssMissing ? "#7f1d1d" : "#064e3b",
            color: cssMissing ? "#fecaca" : "#a7f3d0",
          }}
        >
          {cssMissing ? "ALARM (alle 3 rot)" : "OK (≥ 1 grün)"}
        </span>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 10, margin: "0 0 8px" }}>
        Alarm wird nur ausgelöst, wenn ALLE drei Sentinels gleichzeitig versagen — so vermeiden wir
        False Positives durch Tailwind-Purging oder cross-origin Stylesheets.
      </p>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {sentinels.map((s) => (
          <li
            key={s.label}
            style={{
              padding: "6px 0",
              borderTop: "1px solid #1e293b",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ color: "#cbd5e1", fontSize: 11 }}>
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: s.ok ? "#34d399" : "#f87171",
                    marginRight: 6,
                  }}
                />
                {s.label}
              </span>
              <span
                style={{
                  color: s.ok ? "#34d399" : "#f87171",
                  fontSize: 11,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {s.value}
              </span>
            </div>
            <p style={{ color: "#64748b", fontSize: 10, margin: "2px 0 0 14px" }}>{s.explain}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DebugPanel({
  signals,
  open,
  onToggle,
  onDismiss,
  bannerPref,
  onSetBannerPref,
  localPref,
  onSetLocalPref,
  accountPref,
  onSetAccountPref,
  hasUser,
  onResetPrefs,
}: {
  signals: Signals;
  open: boolean;
  onToggle: () => void;
  onDismiss: () => void;
  bannerPref: "on" | "off";
  onSetBannerPref: (pref: "on" | "off") => void;
  localPref: "on" | "off";
  onSetLocalPref: (pref: "on" | "off") => void;
  accountPref: "on" | "off";
  onSetAccountPref: (pref: "on" | "off") => void;
  hasUser: boolean;
  onResetPrefs: () => void;
}) {
  // (Funktion ist außerhalb der Komponente definiert.)
  const modeColor =
    signals.buildMode === "prod" ? "#2563eb" : signals.buildMode === "dev" ? "#16a34a" : "#9ca3af";

  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: "flex", gap: 8, padding: "2px 0" }}>
      <span style={{ color: "#94a3b8", minWidth: 150 }}>{label}</span>
      <span style={{ color: "#e2e8f0", wordBreak: "break-all" }}>{value}</span>
    </div>
  );

  const bool = (v: boolean) => (
    <span style={{ color: v ? "#34d399" : "#f87171" }}>{v ? "true" : "false"}</span>
  );

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 2147483646,
        background: "#0f172a",
        color: "#e2e8f0",
        border: "1px solid #334155",
        borderRadius: 8,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 11,
        lineHeight: 1.5,
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        maxWidth: open ? 460 : 220,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 10px",
          borderBottom: open ? "1px solid #334155" : "none",
          background: "#1e293b",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: 999,
            background: modeColor,
          }}
        />
        <span style={{ fontWeight: 600, flex: 1 }}>diag · {signals.buildMode}</span>
        <button
          type="button"
          onClick={onToggle}
          style={{
            background: "transparent",
            color: "#cbd5e1",
            border: "1px solid #475569",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 11,
            padding: "2px 6px",
          }}
        >
          {open ? "−" : "+"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          title="Panel ausblenden (bis Reload)"
          style={{
            background: "transparent",
            color: "#cbd5e1",
            border: "1px solid #475569",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 11,
            padding: "2px 6px",
          }}
        >
          ×
        </button>
      </div>
      {open && (
        <div style={{ padding: "8px 10px" }}>
          <SentinelSection signals={signals} />
          {row("Build-Modus", signals.buildMode)}
          {row("vite dev marker", bool(signals.hasViteDevMarker))}
          {row("prod asset.js", bool(signals.hasProdAssetJs))}
          {row("prod asset.css", bool(signals.hasProdAssetCss))}
          {row("prod modulepreload", bool(signals.hasProdPreload))}
          {row("stylesheets (links)", signals.stylesheetLinks)}
          {row("scripts gesamt", signals.scriptCount)}
          {row("translated", bool(signals.translated))}
          {row(
            "adblock bait fetch",
            <span
              style={{
                color:
                  signals.adblock.baitFetch === "blocked"
                    ? "#f87171"
                    : signals.adblock.baitFetch === "ok"
                      ? "#34d399"
                      : "#facc15",
              }}
            >
              {signals.adblock.baitFetch}
            </span>,
          )}
          {row("adblock bait element hidden", bool(signals.adblock.baitElementHidden))}
          {row("resource timing /assets/", signals.adblock.resourceTimingAssets)}
          {row(
            "resource timing blocked",
            <span
              style={{
                color: signals.adblock.resourceTimingBlocked > 0 ? "#f87171" : "#34d399",
              }}
            >
              {signals.adblock.resourceTimingBlocked}
            </span>,
          )}
          {row("browser", signals.adblock.browser)}
          {row(
            "same-origin (blocked/total)",
            <span
              style={{
                color: signals.adblock.sameOriginBlocked > 0 ? "#f87171" : "#34d399",
              }}
            >
              {signals.adblock.sameOriginBlocked}/{signals.adblock.sameOriginResources}
            </span>,
          )}
          {row(
            "3rd-party (blocked/total)",
            <span
              style={{
                color: signals.adblock.thirdPartyBlocked > 0 ? "#facc15" : "#34d399",
              }}
            >
              {signals.adblock.thirdPartyBlocked}/{signals.adblock.thirdPartyResources}
            </span>,
          )}
          {row(
            "confidence",
            <span
              style={{
                color:
                  signals.adblock.confidence >= 60
                    ? "#f87171"
                    : signals.adblock.confidence >= 30
                      ? "#facc15"
                      : "#34d399",
                fontWeight: 700,
              }}
            >
              {signals.adblock.confidence}/100
            </span>,
          )}
          {row("captured at", signals.capturedAt)}

          {signals.adblock.confidenceBreakdown.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>
                confidence breakdown ({signals.adblock.confidenceBreakdown.length})
              </summary>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {signals.adblock.confidenceBreakdown.map((b) => (
                  <li key={b.signal} style={{ wordBreak: "break-all" }}>
                    <span style={{ color: "#facc15" }}>+{b.weight}</span> {b.signal}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {signals.adblock.blockedOrigins.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>
                blocked origins ({signals.adblock.blockedOrigins.length})
              </summary>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {signals.adblock.blockedOrigins.map((o) => (
                  <li key={o} style={{ wordBreak: "break-all" }}>
                    {o}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {Object.keys(signals.adblock.trackerProbes).length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>
                tracker probes ({Object.keys(signals.adblock.trackerProbes).length})
              </summary>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {Object.entries(signals.adblock.trackerProbes).map(([url, state]) => (
                  <li key={url} style={{ wordBreak: "break-all" }}>
                    <span
                      style={{
                        color: state === "blocked" ? "#f87171" : "#34d399",
                      }}
                    >
                      {state}
                    </span>{" "}
                    {url.replace(/^https?:\/\//, "")}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <WhyBreakdown classified={signals.adblock.classified} />

          {signals.viteScripts.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>
                vite scripts ({signals.viteScripts.length})
              </summary>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {signals.viteScripts.map((s) => (
                  <li key={s} style={{ wordBreak: "break-all" }}>
                    {s}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {signals.prodScripts.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>
                prod scripts ({signals.prodScripts.length})
              </summary>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {signals.prodScripts.map((s) => (
                  <li key={s} style={{ wordBreak: "break-all" }}>
                    {s}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {signals.prodPreloads.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>
                prod modulepreloads ({signals.prodPreloads.length})
              </summary>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {signals.prodPreloads.map((s) => (
                  <li key={s} style={{ wordBreak: "break-all" }}>
                    {s}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div style={{ marginTop: 8, color: "#64748b" }}>
            Aktivieren mit <code>?diag=1</code> oder <code>localStorage.diag = "1"</code>. Snapshot
            liegt auch in der Konsole unter <code>[DiagnosticBanner] signals</code>.
          </div>

          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid #334155",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "#94a3b8", flex: 1 }}>Snapshot exportieren</span>
            <button
              type="button"
              onClick={() => downloadSignalsJson(signals)}
              title="Lädt den aktuellen Signal-Snapshot als JSON-Datei herunter"
              style={{
                background: "#2563eb",
                color: "#fff",
                border: 0,
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 11,
                padding: "3px 10px",
              }}
            >
              ⬇ JSON herunterladen
            </button>
          </div>

          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid #334155",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "#94a3b8", flex: 1 }}>Banner in dieser Session</span>
            <button
              type="button"
              onClick={() => onSetBannerPref(bannerPref === "on" ? "off" : "on")}
              style={{
                background: bannerPref === "on" ? "#16a34a" : "#475569",
                color: "#fff",
                border: 0,
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 11,
                padding: "3px 10px",
              }}
            >
              {bannerPref === "on" ? "Sichtbar" : "Ausgeblendet"}
            </button>
          </div>

          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "#94a3b8", flex: 1 }} title="Persistent in diesem Browser">
              Banner lokal (dieser Browser)
            </span>
            <button
              type="button"
              onClick={() => onSetLocalPref(localPref === "on" ? "off" : "on")}
              style={{
                background: localPref === "on" ? "#16a34a" : "#475569",
                color: "#fff",
                border: 0,
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 11,
                padding: "3px 10px",
              }}
            >
              {localPref === "on" ? "Sichtbar" : "Ausgeblendet"}
            </button>
          </div>

          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{ color: "#94a3b8", flex: 1 }}
              title={
                hasUser
                  ? "Persistent für dieses Konto in diesem Browser"
                  : "Erst nach Login verfügbar"
              }
            >
              Banner pro Konto
            </span>
            <button
              type="button"
              disabled={!hasUser}
              onClick={() => onSetAccountPref(accountPref === "on" ? "off" : "on")}
              style={{
                background: !hasUser ? "#1e293b" : accountPref === "on" ? "#16a34a" : "#475569",
                color: !hasUser ? "#64748b" : "#fff",
                border: 0,
                borderRadius: 4,
                cursor: hasUser ? "pointer" : "not-allowed",
                fontSize: 11,
                padding: "3px 10px",
              }}
            >
              {!hasUser ? "Nicht angemeldet" : accountPref === "on" ? "Sichtbar" : "Ausgeblendet"}
            </button>
          </div>

          <div
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "#94a3b8", flex: 1 }}>Alle Banner-Einstellungen</span>
            <button
              type="button"
              onClick={onResetPrefs}
              title="Setzt Session-, Lokal- und Konto-Präferenz auf Standard (sichtbar) zurück"
              style={{
                background: "#0ea5e9",
                color: "#fff",
                border: 0,
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 11,
                padding: "3px 10px",
              }}
            >
              ↺ Auf Standard zurücksetzen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Zeigt eine gruppierte "Warum?"-Aufschlüsselung aller beobachteten
 * Ressourcen: welche URLs als synthetische Probes oder Non-Critical-Pfade
 * ausgeschlossen wurden und welche App-Bundles wirklich gefehlt haben.
 */
function WhyBreakdown({ classified }: { classified: ClassifiedResource[] }) {
  if (!classified.length) return null;

  const groups: Array<{
    key: ClassifiedResource["category"];
    label: string;
    color: string;
    items: ClassifiedResource[];
  }> = [
    {
      key: "app-bundle",
      label: "App-Bundles geblockt",
      color: "#f87171",
      items: classified.filter((c) => c.category === "app-bundle" && c.blocked),
    },
    {
      key: "synthetic-probe",
      label: "Synthetische Probes (ausgeschlossen)",
      color: "#a78bfa",
      items: classified.filter((c) => c.category === "synthetic-probe"),
    },
    {
      key: "non-critical",
      label: "Non-Critical (ausgeschlossen)",
      color: "#94a3b8",
      items: classified.filter((c) => c.category === "non-critical"),
    },
    {
      key: "other",
      label: "Sonstige 0/0 (informativ)",
      color: "#facc15",
      items: classified.filter((c) => c.category === "other" && c.blocked),
    },
    {
      key: "app-bundle",
      label: "App-Bundles erfolgreich",
      color: "#34d399",
      items: classified.filter((c) => c.category === "app-bundle" && !c.blocked),
    },
  ];

  return (
    <details style={{ marginTop: 6 }}>
      <summary style={{ cursor: "pointer", color: "#94a3b8" }}>
        Warum? — Ressourcen-Klassifizierung ({classified.length})
      </summary>
      <div style={{ marginTop: 4 }}>
        {groups
          .filter((g) => g.items.length > 0)
          .map((g, idx) => (
            <details key={`${g.key}-${idx}`} style={{ marginTop: 4 }}>
              <summary style={{ cursor: "pointer", color: g.color }}>
                {g.label} ({g.items.length})
              </summary>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {g.items.slice(0, 25).map((c) => (
                  <li key={c.url} style={{ wordBreak: "break-all", marginBottom: 2 }}>
                    <span style={{ color: g.color }}>●</span>{" "}
                    <span style={{ color: "#e2e8f0" }}>{c.url.replace(/^https?:\/\//, "")}</span>
                    <div style={{ color: "#64748b", paddingLeft: 12 }}>
                      {c.origin} · {c.reason}
                    </div>
                  </li>
                ))}
                {g.items.length > 25 && (
                  <li style={{ color: "#64748b" }}>… und {g.items.length - 25} weitere</li>
                )}
              </ul>
            </details>
          ))}
      </div>
    </details>
  );
}
